import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";

import type { GeneratedReportSummary } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatPeriodLabel, formatTimestamp } from "@/lib/utils";

interface ReportCardProps {
  report: GeneratedReportSummary;
}

export function ReportCard({ report }: ReportCardProps) {
  const isPending = !report.pdf_available;
  const pdfHref = `/api/v1/reports/${report.id}/pdf`;
  const htmlHref = `/api/v1/reports/${report.id}/html`;

  return (
    <div className="rounded-md border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <FileText className="mt-1 size-5 text-muted-foreground" />
          <div>
            <h3 className="text-base font-semibold">
              {formatPeriodLabel(report.period_start)}
            </h3>
            <p className="text-xs text-muted-foreground">
              {report.report_type === "monthly"
                ? "Reporte mensual"
                : report.report_type}{" "}
              · Generado por <strong>{report.generated_by}</strong> el{" "}
              {formatTimestamp(report.generated_at)}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {isPending ? (
                <Badge variant="secondary" className="gap-1">
                  <Loader2 className="size-3 animate-spin" /> Generando…
                </Badge>
              ) : (
                <Badge className="bg-green-600 text-white hover:bg-green-600">
                  Listo
                </Badge>
              )}
              {report.email_sent && (
                <Badge variant="outline">Email enviado</Badge>
              )}
              {report.ai_tokens_used > 0 && (
                <Badge variant="outline" className="text-xs">
                  {report.ai_tokens_used.toLocaleString()} tokens
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          <Button
            asChild
            disabled={isPending}
            variant="default"
            size="sm"
            className={isPending ? "pointer-events-none opacity-50" : ""}
          >
            <a
              href={isPending ? undefined : pdfHref}
              aria-disabled={isPending}
              data-testid="download-pdf"
            >
              <Download className="size-4" /> PDF
            </a>
          </Button>
          <Button
            asChild
            disabled={isPending}
            variant="outline"
            size="sm"
            className={isPending ? "pointer-events-none opacity-50" : ""}
          >
            <a
              href={isPending ? undefined : htmlHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={isPending}
              data-testid="view-html"
            >
              <ExternalLink className="size-4" /> Ver HTML
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
